import { useMemo, useState } from "react";
import { toast } from "../../utils/toast";
import { InfoBox, Input, SearchableSelect, Textarea } from "../ui";
import CallFields from "../calls/CallFields";
import SaveChangesBar from "../calls/SaveChangesBar";
import { useFieldChanges } from "../calls/forms";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { TYPE_LABELS, closesAtOf, countryOptionsOf, destinationOf, hasDestination, kigaliDate, kindOf, needsSponsor, opensAtOf, sponsorOptionsOf, useOptions, useScholarshipChange, useSponsors } from "./model";
import { canManageScholarships, needsReason, setUpOpen } from "./access";
import EvidenceCard, { EVIDENCE } from "./EvidenceCard";

const NO_REASON = { reason: "" };
const MIN_REASON = 10;

// The form's view of a scholarship; dates are Kigali calendar days
const toForm = (s) => ({
  name: s.name ?? "",
  sponsorId: s.sponsor?.id ?? "",
  academicYearId: s.academicYear?.id ?? "",
  destinationCountryCode: s.destinationCountryCode ?? "",
  opensDate: kigaliDate(s.opensAt),
  closesDate: kigaliDate(s.closesAt),
  awardQuota: s.awardQuota == null ? "" : String(s.awardQuota),
  visibility: s.visibility ?? "PUBLIC",
  publicSummary: s.publicSummary ?? "",
  description: s.description ?? "",
});

// Each form field as the API expects it in a PATCH
const API_VALUE = {
  name: (v) => ["name", v.trim()],
  sponsorId: (v) => ["sponsorId", v || null],
  academicYearId: (v) => ["academicYearId", v],
  destinationCountryCode: (v) => ["destinationCountryCode", v],
  opensDate: (v) => ["opensAt", v && opensAtOf(v)],
  closesDate: (v) => ["closesAt", v && closesAtOf(v)],
  awardQuota: (v) => ["awardQuota", v === "" ? null : Number(v)],
  visibility: (v) => ["visibility", v],
  publicSummary: (v) => ["publicSummary", v.trim()],
  description: (v) => ["description", v.trim()],
};

const FORM_FIELD = { opensAt: "opensDate", closesAt: "closesDate" };

// Which fields can change: everything while the set-up is open, then only a later closing date
// while published. The backend has the final say (409 field-locked).
const editableOf = (s) => {
  if (setUpOpen(s)) return () => true;
  if (s.state === "PUBLISHED") return (field) => field === "closesDate";
  return () => false;
};

// A draft needs only its name (the rest is checked when publishing); a published call keeps every detail
export const validateDetails = (values, original, s) => {
  const errors = {};
  const draft = s.state === "DRAFT";
  if (!values.name.trim()) errors.name = "Name is required";
  if (!draft && !values.description.trim()) errors.description = "Description is required";
  if (!draft && !values.opensDate) errors.opensDate = "Opening date is required";
  if (!draft && !values.closesDate) errors.closesDate = "Closing date is required";
  else if (values.opensDate && values.closesDate && values.closesDate < values.opensDate) errors.closesDate = "Closing date must be on or after the opening date";
  else if (!setUpOpen(s) && values.closesDate < original.closesDate) errors.closesDate = "Students have started applying: the closing date can only move later";
  if (values.awardQuota !== "" && !(Number.isInteger(Number(values.awardQuota)) && Number(values.awardQuota) >= 1)) errors.awardQuota = "Enter a whole number of 1 or more";
  if (needsReason(s) && values.reason.trim().length < MIN_REASON) errors.reason = `Explain the change in at least ${MIN_REASON} characters`;
  return errors;
};

// Details the API cannot clear: emptied in a draft, they are left as saved
const NOT_CLEARED = ["academicYearId", "destinationCountryCode", "opensAt", "closesAt", "description"];

// The PATCH body of the changed fields
export const detailsBodyOf = (values, changedFields) =>
  Object.fromEntries(changedFields.map((field) => API_VALUE[field](values[field])).filter(([name, value]) => !(NOT_CLEARED.includes(name) && !value)));

export const toDetailsForm = toForm;

/**
 * Journey step 2: the scholarship's details. Unsaved edits are kept per scholarship (only the
 * fields the admin changed, never the loaded data). A draft saves without a reason; once published
 * every save needs one, and once a student starts applying only a later closing date can be set.
 */
export default function DetailsTab({ scholarship }) {
  const { one } = kindOf(scholarship.type);
  const change = useScholarshipChange(scholarship.id);
  const original = useMemo(() => toForm(scholarship), [scholarship]);
  const { values, changedFields, errors, setErrors, update, discard } = useFieldChanges(`scholarship-edit:${scholarship.id}`, original, NO_REASON);
  const [saving, setSaving] = useState(false);

  // Settings decide the lists; the scholarship's saved year and destination stay selectable
  const { data: options } = useOptions();
  const { data: sponsors = [] } = useSponsors();
  const sponsorOptions = useMemo(() => sponsorOptionsOf(sponsors, scholarship.sponsor), [sponsors, scholarship.sponsor]);
  const countryOptions = useMemo(() => countryOptionsOf(options?.destinationCountries, scholarship.destinationCountryCode), [options, scholarship.destinationCountryCode]);

  const manage = canManageScholarships();
  const canEdit = manage ? editableOf(scholarship) : () => false;
  const asksReason = needsReason(scholarship);
  const nothingEditable = !Object.keys(API_VALUE).some(canEdit);
  // Once students apply, only the closing date can still move, and only later
  const closingDateOnly = manage && scholarship.state !== "DRAFT" && !setUpOpen(scholarship) && !nothingEditable;

  const save = async (e) => {
    e.preventDefault();
    const found = validateDetails(values, original, scholarship);
    setErrors(found);
    if (Object.keys(found).length) return;

    const body = detailsBodyOf(values, changedFields);
    if (asksReason) body.reason = values.reason.trim();
    setSaving(true);
    try {
      await change((etag) => scholarshipsApi.patch(`admin/scholarships/${scholarship.id}`, body, { ifMatch: etag }));
      toast.success("Details saved");
      discard();
    } catch (error) {
      console.error(`Failed to update scholarship ${scholarship.id}:`, error);
      const serverErrors = fieldErrorsOf(problemOf(error));
      setErrors(Object.fromEntries(Object.entries(serverErrors).map(([field, message]) => [FORM_FIELD[field] ?? field, message])));
      toast.error(errorMessageOf(error, `Failed to save the ${one}. Please try again.`));
    } finally {
      setSaving(false);
    }
  };

  const field = (name) => ({ name, value: values[name], onChange: update, error: errors[name], disabled: saving || !canEdit(name) });

  return (
    <form onSubmit={save} noValidate className="flex flex-col gap-5">
      {closingDateOnly && <InfoBox>Only a later closing date can be set.</InfoBox>}

      <CallFields
        draft={scholarship.state === "DRAFT"}
        field={field}
        savedYear={scholarship.academicYear}
        quotaLabel="Number of awards"
        summaryMax={2000}
        descriptionMax={10000}
        closesMin={setUpOpen(scholarship) ? values.opensDate : original.closesDate}
        belowVisibility={values.visibility === "PRIVATE" && <EvidenceCard scholarship={scholarship} kind={EVIDENCE.privacy} />}
      >
        {/* Fixed at creation: a scholarship never becomes a loan */}
        <Input label="Type" value={TYPE_LABELS[scholarship.type] ?? scholarship.type} disabled readOnly />
        {needsSponsor(scholarship.type) && <SearchableSelect label="Partner" placeholder="Type or select the partner" options={sponsorOptions} {...field("sponsorId")} />}
        {/* An abroad loan student names their own country */}
        {!hasDestination(scholarship.type) ? (
          <Input label="Destination country" value={destinationOf(scholarship)} disabled readOnly />
        ) : (
          <SearchableSelect label="Destination country" required={scholarship.state !== "DRAFT"} placeholder="Type or select a country" options={countryOptions} {...field("destinationCountryCode")} />
        )}
      </CallFields>

      {!nothingEditable && (
        <div className="flex flex-col gap-4 border-t border-slate-100 pt-5">
          {asksReason && (
            <Textarea
              label="Reason for this change"
              name="reason"
              required
              rows={2}
              maxLength={1000}
              placeholder="Why are these details changing?"
              value={values.reason}
              onChange={update}
              error={errors.reason}
              disabled={saving}
            />
          )}
          <SaveChangesBar count={changedFields.length} saving={saving} canDiscard={changedFields.length > 0 || Boolean(values.reason)} onDiscard={discard} />
        </div>
      )}
    </form>
  );
}
